import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { completeRequiredPasswordChange } from '@/lib/auth/client-auth-service';

function AccountStateCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-bg-app flex items-center justify-center p-6">
      <Card className="w-full max-w-[380px] text-center space-y-4">
        <div className="w-14 h-14 rounded-2xl bg-aura-primary/10 text-aura-primary flex items-center justify-center mx-auto text-xl font-black">
          PK
        </div>
        <h1 className="text-xl font-black text-text-main">{title}</h1>
        <div className="text-sm text-text-muted leading-relaxed">{children}</div>
        <Link to="/auth" className="inline-block w-full">
          <Button variant="secondary" fullWidth>
            Back to sign in
          </Button>
        </Link>
      </Card>
    </div>
  );
}

export function AccountPendingPage() {
  return (
    <AccountStateCard title="Access Pending">
      Ask an admin to activate your Poker Knights profile before using the app.
    </AccountStateCard>
  );
}

export function AccountDisabledPage() {
  return (
    <AccountStateCard title="Account Disabled">
      This profile is disabled. Contact your host if you expected to have access.
    </AccountStateCard>
  );
}

export function AccountConflictPage() {
  return (
    <AccountStateCard title="Conflict Review">
      This sign-in matches an identity that needs admin review before it can be used.
    </AccountStateCard>
  );
}

export function AccountSetupPage() {
  return (
    <AccountStateCard title="Account Setup">
      Your invite needs to be completed before the ordinary poker workflows are available.
    </AccountStateCard>
  );
}

export function AccountSecurityPage() {
  const navigate = useNavigate();
  const { firebaseUser } = useAuth();
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  if (!firebaseUser) {
    return (
      <AccountStateCard title="Account Security">
        Sign in before updating your password.
      </AccountStateCard>
    );
  }

  const submitPasswordChange = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    setError(null);
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Fill in all password fields.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Use at least 8 characters for the new password.');
      return;
    }

    setSubmitting(true);
    try {
      await completeRequiredPasswordChange(currentPassword, newPassword);
      setMessage('Password updated.');
      navigate('/setup', { replace: true });
    } catch {
      setError('Password update failed. Sign in again or use forgot password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-app flex items-center justify-center p-6">
      <Card className="w-full max-w-[380px] space-y-4">
        <div className="w-14 h-14 rounded-2xl bg-aura-primary/10 text-aura-primary flex items-center justify-center mx-auto text-xl font-black">
          PK
        </div>
        <h1 className="text-xl font-black text-text-main text-center">Account Security</h1>
        {firebaseUser?.email && (
          <div className="text-center text-xs font-semibold text-text-muted">
            {firebaseUser.email}
          </div>
        )}
        <form className="space-y-3" onSubmit={submitPasswordChange}>
          <PasswordField
            id="account-current-password"
            label="Current password"
            value={currentPassword}
            autoComplete="current-password"
            onChange={setCurrentPassword}
          />
          <PasswordField
            id="account-new-password"
            label="New password"
            value={newPassword}
            autoComplete="new-password"
            onChange={setNewPassword}
          />
          <PasswordField
            id="account-confirm-new-password"
            label="Confirm new password"
            value={confirmPassword}
            autoComplete="new-password"
            onChange={setConfirmPassword}
          />
          {message && (
            <div className="rounded-xl bg-aura-accent/20 px-4 py-3 text-sm font-semibold text-aura-primary">
              {message}
            </div>
          )}
          {error && (
            <div className="rounded-xl bg-error/10 px-4 py-3 text-sm font-semibold text-error">
              {error}
            </div>
          )}
          <Button type="submit" variant="primary" fullWidth disabled={submitting}>
            {submitting ? 'Updating...' : 'Update password'}
          </Button>
        </form>
        <Link to="/auth" className="inline-block w-full">
          <Button type="button" variant="secondary" fullWidth>
            Back to sign in
          </Button>
        </Link>
      </Card>
    </div>
  );
}

function PasswordField({
  autoComplete,
  id,
  label,
  onChange,
  value,
}: {
  autoComplete: string;
  id: string;
  label: string;
  onChange: (value: string) => void;
  value: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-semibold text-text-muted mb-1.5">
        {label}
      </label>
      <input
        id={id}
        type="password"
        value={value}
        autoComplete={autoComplete}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-xl border border-aura-text/10 px-4 py-3 text-sm outline-none focus:border-aura-primary"
      />
    </div>
  );
}
